import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { createLawImplementationFeedback } from "services/lawImplementationService";
import { createLegalFeedback } from "services/legalAdvisoryService";
import { createLegalAwarenessFeedback } from "services/legalAwarenessService";
import { createFeedback } from "services/legalResearchService";
import { createLegalTrainingProvisionFeedback } from "services/legalTrainingProvision";
import { createLegalTranslationFeedback } from "services/legalTranslationService";
import { createLegislativeFeedback } from "services/legislativeService";
import { createRegisterDirective, createRegisterDirectiveFeedback } from "services/registerDirectivesService";
import { createSectorDirectiveFeedback } from "services/sectorDirectives";
import { FeedbackAllowedRoles } from "utils/constantValues";
import { getUserType } from "utils/helpers";

interface Feedback {
    id: number;
    research_request: number;
    given_by: string;
    role: string;
    feedback_type: string;
    note: string;
    status: string;
}

function ResponseInput({ request, onSubmit, type }: { type?: any; request: any; onSubmit: (response: any) => void }) {
    const { t } = useTranslation();
    const [responseText, setResponseText] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const contextData = useAuth();
    const { showToast } = useToast();
    const location = useLocation();
    const isInternal = location.pathname.includes("/internal/");
    const isCommittee =
        location.pathname.includes("/committee") || !!request?.request_summary;

    const handleSubmit = async () => {
        if (!responseText.trim()) return;
        const data = new FormData();
        console.log("username: ", contextData?.user?.username);
        if (type === "legislative_drafting")
            data.append("instruction", request.id);
        else if (type === "legal")
            data.append("advisory_request", request.id);
        else if (type === "awareness")
            data.append("awareness", request.id);
        else if (type === "training")
            data.append("training_request", request.id);
        else if (type === "translation")
            data.append("request", request.id);
        else if (type === "sector")
            data.append("request", request.id);
        else if (type === "register")
            data.append("request", request.id);
        else if (type === "law")
            data.append("followup", request.id);
        else if (type === "audit")
            data.append("audit_folloup", request.id);
        else
            if (isInternal)
                data.append("internal_research_request", request.id);
            else if (isCommittee)
                data.append("research_committe_report", request.id);
            else
                data.append("research_request", request.id);
        data.append("given_by", contextData?.user?.full_name || "");
        const feedbackRole = contextData?.user?.roles.find((role: string) => FeedbackAllowedRoles.has(role))
        if (feedbackRole)
            data.append("role", getUserType(feedbackRole));
        data.append("feedback_type", "other");
        data.append("note", responseText);
        data.append("summary", responseText);
        data.append("status", "pending");

        try {
            setSubmitting(true);

            let response: any;
            if (type === "legislative_drafting")
                response = await createLegislativeFeedback(data);
            else if (type === "legal")
                response = await createLegalFeedback(data);
            else if (type === "awareness")
                response = await createLegalAwarenessFeedback(data);
            else if (type === "training")
                response = await createLegalTrainingProvisionFeedback(data);
            else if (type === "sector")
                response = await createSectorDirectiveFeedback(data);
            else if (type === "register")
                response = await createRegisterDirectiveFeedback(contextData.token || contextData.user?.access_token || "", data);
            else if (type === "translation")
                response = await createLegalTranslationFeedback(data);
            else if (type === "law" || type === "audit")
                response = await createLawImplementationFeedback(data);
            else
                response = await createFeedback(contextData.token, data);

            console.log(response);
            onSubmit((prev: Feedback[]) => [
                ...prev,
                response,
            ]);
            showToast(t("FeedbackSubmittedSuccess"), "success");

            setResponseText("");
        } catch (err) {
            console.error("Error sending feedback:", err);
            showToast(t("FeedbackNotSubmitted"), "error");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="mt-auto pt-4 border-t border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-2">
                {t("WriteYourFeedbackHere")}
            </h3>

            <textarea
                value={responseText}
                onChange={(e) => setResponseText(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                placeholder={t("WriteHere")}
            ></textarea>

            <div className="flex justify-end mt-3">
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className={`px-6 py-2 rounded-md transition-colors text-white ${submitting ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-200"
                        }`}
                >
                    {submitting ? t("Sending") : t("SendFeedback")}
                </button>
            </div>
        </div>
    );
}

export default ResponseInput;